import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import type { MemberBasicInfo } from '../services/enrollment.service';

interface DuplicateMemberDialogProps {
  open: boolean;
  onClose: () => void;
  memberInfo: MemberBasicInfo | null;
}

export default function DuplicateMemberDialog({
  open,
  onClose,
  memberInfo,
}: DuplicateMemberDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 28 }} />
        <Typography variant="h5" component="span" fontWeight="bold">
          {t('duplicate.title')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('duplicate.message')}
        </Typography>

        {memberInfo && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PersonIcon sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" fontWeight="bold">
                {t('duplicate.memberInfo')}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">{t('duplicate.name')}:</Typography>
                <Typography fontWeight="medium">
                  {memberInfo.firstName} {memberInfo.lastName}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">{t('duplicate.phone')}:</Typography>
                <Typography fontWeight="medium">{memberInfo.phone}</Typography>
              </Box>

              {memberInfo.email && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">{t('duplicate.email')}:</Typography>
                  <Typography fontWeight="medium">{memberInfo.email}</Typography>
                </Box>
              )}

              {memberInfo.memberSince && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">{t('duplicate.memberSince')}:</Typography>
                  <Typography fontWeight="medium">{memberInfo.memberSince}</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        <Paper
          sx={{
            mt: 3,
            p: 2,
            bgcolor: 'info.light',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="info.dark" fontWeight="medium">
            {t('duplicate.contactStaff')}
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="contained"
          onClick={onClose}
          size="large"
          fullWidth
        >
          {t('duplicate.changePhone')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
